import React, {useEffect, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import {
  fetchTeamsAndSetState,
  initiateLinearOAuth,
  saveTeamMembers,
} from '../../../../Integrations/Linear'
import useCrispChat from 'app/Hooks/useCrispChat'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import InfoCard from '../drawer-components/InfoCard'
import {DrawerContent} from '../drawer-components/DrawerContent'
import {AddDrawerIntegrationProps} from '../utils/types'
import ConfirmationModal from 'app/components/ConfirmationModal'

const LinearDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const [teamName, setTeamName] = useState('')
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const {currentUser} = useAuth()
  const [integration, setIntegration] = useState<any[]>([])
  const [teams, setTeams] = useState<any[]>([])
  const {toggleCrispChat} = useCrispChat()
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null)
  const [isTeamLocked, setIsTeamLocked] = useState(false)

  useEffect(() => {
    if (isOpen && id) {
      getIntegration()
    }
  }, [id, isOpen])

  useEffect(() => {
    if (isOpen && connected && !isLoading && !isTeamLocked) {
      if (!teamName) {
        setFormErrors((prev) => ({ ...prev, teamName: 'Please select a team' }));
      } else {
        setFormErrors((prev) => ({ ...prev, teamName: '' }));
      }
    } else {
      setFormErrors((prev) => ({ ...prev, teamName: '' }));
    }
  }, [teamName, isOpen, connected, isLoading, isTeamLocked]);

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) {
        return
      }
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)

      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const integrationData = {
            ...doc.data(),
            id: doc.id,
            status: doc.data().status,
            teamName: doc.data().teamName,
          }
          setIntegration([integrationData])
          setConnected(integrationData.status === 'Connected')
          if (integrationData.teamName) {
            setTeamName(integrationData.teamName || '')
            setIsTeamLocked(true)
          }
        }
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  useEffect(() => {
    if (integration.length > 0) {
      const fetchTeams = async () => {
        const teams = await fetchTeamsAndSetState(integration[0].access_token)
        if (teams) {
          setTeams(teams)
        }
      }
      fetchTeams()
    }
  }, [integration])

  const validateForm = (teamName: string) => {
    const errors: Record<string, string> = {};
  
    if (!teamName.trim()) {
      errors.teamName = 'Team Name is required';
    }
  
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTeamName = async (teamName: string) => {
    setIsLoading(true)

    try {
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = `${month}/${day}/${year}`

      if (currentUser && id) {
        const tenantId = currentUser.tenantId || ''
        const docRef = doc(db, 'tenants', tenantId, 'integrations', id)

        const selectedTeam = teams.find((team) => team.name === teamName)
        if (!selectedTeam) {
          showErrorToast({message: 'Selected team not found in available teams'})
          setIsLoading(false)
          return
        }
        const teamId = selectedTeam?.id

        await updateDoc(docRef, {teamName, teamId})

        await saveTeamMembers(integration[0].access_token, teamId, teamName, tenantId, currentUser)

        const tenantRef = doc(db, 'tenants', tenantId)
        const activitiesRef = collection(tenantRef, 'activities')
        await addDoc(activitiesRef, {
          message: `${currentUser?.userName} has updated the ${teamName} team in the Linear integration.`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          action: 'Linear update',
          target: 'Integration',
          email: currentUser?.email,
          username: currentUser?.userName,
          date: todaysDate,
        })
        setIsTeamLocked(true)
        showSuccessToast({message: 'Linear team updated  successfully'})
        window.scrollTo(0, 0)
      }
    } catch (error) {
      console.error('Error updating team name:', error)
      showErrorToast({message: `Failed to update team`})
      setIsTeamLocked(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteIntegration = (item: any) => {
    setSelectedIntegration(item)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDisconnecting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const policyRef = collection(tenantRef, 'integrations')
      const docRef = doc(policyRef, selectedIntegration.id)
      await deleteDoc(docRef)

      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'linear')
      await deleteDoc(accessReviewRef)

      const vendorsRef = collection(tenantRef, 'vendors')
      const linearVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Linear'))
      const linearVendorSnapshot = await getDocs(linearVendorQuery)

      const deleteVendorPromises = linearVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all(deleteVendorPromises)

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Linear integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
      setIsTeamLocked(false)
      localStorage.removeItem('linear_access_token')
      showSuccessToast({
        message: `${selectedIntegration.name} integration has been deleted successfully!`,
      })
      onClose()
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the integration.'})
    } finally {
      setIsDisconnecting(false)
      setIsDeleteModalOpen(false)
      setSelectedIntegration(null)
    }
  }

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
        <DrawerHeader title='Linear' onClose={onClose} />

        <div
          className='scroll-y overflow-auto p-6 mb-4'
          tabIndex={-1}
          style={{height: 'calc(100vh - 120px)'}}
        >
          <div className='form'>
            <DrawerContent
              title='Linear'
              description='Project Management'
              logo='/integrations/Linear.svg'
              integration={integration}
              openAccordion={openAccordion}
              setOpenAccordion={setOpenAccordion}
              openAccordion2={openAccordion2}
              setOpenAccordion2={setOpenAccordion2}
              permissionInfo='SecureSlate will be granted permissions to read and write Linear issues and projects via OAuth 2.0, respecting user permissions.'
              accessTypeinfo='Requires an active Linear integration with sufficient permissions to access issue and project data.'
            />

            <div className='mb-8'>
              <div className='fs-6 fw-bold text-bold mb-4'>Linear Connected to SecureSlate</div>
              <div className='mb-4'>
                <label className='fw-semibold mb-2 fs-6'>Team Name</label>
                <select
                  className={`form-select ${formErrors.teamName ? 'is-invalid' : ''}`}
                  value={teamName || integration[0]?.teamName || ''}
                  onChange={(e) => {
                    const selectedTeamName = e.target.value;
                    setTeamName(selectedTeamName);
                    if (selectedTeamName.trim()) {
                      const updatedErrors = {...formErrors}
                      delete updatedErrors.teamName
                      setFormErrors(updatedErrors)
                    }
                    if (validateForm(selectedTeamName)) {
                      handleTeamName(selectedTeamName);
                    }
                  }}
                  disabled={!connected || isLoading || isTeamLocked}
                >
                  <option value=''>Select Team</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
                {formErrors.teamName && <span className='text-danger'>{formErrors.teamName}</span>}
              </div>
              
              <InfoCard
                type='info'
                title='Need help connecting Linear to SecureSlate?'
                description={
                  <div>
                    Take a look at <a href='#'>our guide</a> for Linear integration with
                    SecureSlate. Need further assistance?{' '}
                    <a href='#' onClick={toggleCrispChat}>
                      Send us a chat.
                    </a>
                  </div>
                }
              />
            </div>
          </div>
        </div>
        <div
          className='offcanvas-footer d-flex justify-content-end border-top p-4'
          style={{position: 'sticky', bottom: 0, backgroundColor: 'white'}}
        >
          {connected ? (
            isDisconnecting ? (
              <button
                type='button'
                className='btn btn-sm fs-6'
                disabled
                style={{background: '#FDEAEC', color: '#EE3043', height: '40px'}}
              >
                Disconnecting...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </button>
            ) : (
              <button
                type='button'
                className='btn btn-sm fs-6'
                onClick={() => handleDeleteIntegration(integration[0])}
                style={{background: '#FDEAEC', color: '#EE3043', height: '40px'}}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F9D5D9')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#FDEAEC')}
              >
                Disconnect
              </button>
            )
          ) : isLoading ? (
            <button
              type='submit'
              value='Submit'
              className='btn btn-sm fs-6'
              style={{backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px'}}
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#148363')
              }
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#0E9F6E')
              }
            >
              Connecting...{' '}
              <span className='spinner-border spinner-border-sm align-middle m-2'></span>
            </button>
          ) : (
            <button
              type='submit'
              value='Submit'
              className='btn btn-sm fs-6'
              onClick={initiateLinearOAuth}
              style={{backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px'}}
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#148363')
              }
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#0E9F6E')
              }
            >
              Connect
            </button>
          )}
        </div>
      </Drawer>
      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleConfirmDelete}
          toDeleteItemTitle={`${selectedIntegration?.name} integration`}
          isLoading={isDisconnecting}
          title={`Remove ${selectedIntegration?.name} integration`}
          message={`Are you sure you want to remove ${selectedIntegration?.name} integration? This action cannot be undone.`}
          type='delete'
          confirmButtonText='Remove'
        />
      )}
    </>
  )
}

export default LinearDrawer
